// Visit-related types shared between API and frontend

// ==================== VIRTUAL OFFICE ID ====================
// Доп.офисы используют виртуальный ID: 999000000 + Office.code
// Это сохраняет совместимость с legacy системой (999000XXX)

export const ADDITIONAL_OFFICE_PREFIX = 999000000;

export function isAdditionalOfficeId(id: number): boolean {
  return id >= ADDITIONAL_OFFICE_PREFIX;
}

export function getOfficeCodeFromVirtualId(virtualId: number): number {
  return virtualId - ADDITIONAL_OFFICE_PREFIX;
}

export function makeVirtualOfficeId(officeCode: number): number {
  return ADDITIONAL_OFFICE_PREFIX + officeCode;
}

// ==================== TYPES ====================

export type VisitStatus = 'PENDING' | 'ACTIVE' | 'VISITED' | 'SKIPPED' | 'CANCELLED';
export type VisitPriority = 'NORMAL' | 'URGENT';
export type OwnershipType = 'own' | 'delegated' | 'subordinate' | 'additionalOffice';
export type OwnershipFilterValue = 'own' | 'subordinates' | 'delegated' | 'additionalOffice';
export type OwnershipFilter = OwnershipFilterValue | OwnershipFilterValue[] | 'all';
export type DeadlineStatus = 'overdue' | 'today' | 'tomorrow' | null;

export interface ClientBrief {
  id: number;
  name: string;
  legalName: string | null;
  // Parking coordinates for main office (for route optimization)
  parkingLat: number | null;
  parkingLon: number | null;
}

export interface OfficeBrief {
  id: string;
  code: number;
  name: string;
}

// Сотрудник, обслуживающий доп.офис (из InvoiceOffice.employeeId)
export interface ServicedByEmployee {
  id: string;
  firstName: string;
  lastName: string;
}

export interface WeeklyVisit {
  id: string;
  status: VisitStatus;
  priority: VisitPriority;
  visitedAt: string | null;
  note: string | null;
  deadline: string | null;
  plannedDate: string | null; // Запланированная дата визита (YYYY-MM-DD)
  plannedTime: string | null; // Запланированное время визита (HH:MM)
  employee: {                 // Кто сделал визит
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

export interface ClientPreference {
  preferredDayOfWeek: number | null;
  preferredTime: string | null;
  note: string | null;
}

export interface PlannedVisitBrief {
  id: string;
  startTime: string | null;
  durationMinutes: number | null;
}

export interface WeeklyVisitItem {
  client: ClientBrief;
  ownershipTypes: OwnershipType[];
  visit: WeeklyVisit | null;
  preference: ClientPreference | null;
  plannedForToday: PlannedVisitBrief | null;
  isOverdue: boolean;
  isPreferredDayToday: boolean;
  deadlineStatus: DeadlineStatus;
  // Новые поля для поддержки доп.офисов
  visitTargetId: number;        // Client.id или 999000000 + Office.code
  displayName: string;          // "Клиент" или "Клиент (Офис)"
  isAdditionalOffice: boolean;  // true если это доп.офис
  office?: OfficeBrief;         // Данные офиса (если доп.офис)
  visitFrequencyWeeks: number;  // Частота визитов (1 = каждую неделю)
  servicedByEmployee?: ServicedByEmployee; // Сотрудник, обслуживающий офис (для доп.офисов)
}

export interface WeeklyVisitsResponse {
  weekCode: string;
  weekRange: string;
  visits: WeeklyVisitItem[];
  delegatedClientIds: number[]; // ID клиентов, делегированных текущему пользователю
}

export interface ActiveVisit {
  id: string;
  client: ClientBrief;
  office?: OfficeBrief;       // Данные офиса (если доп.офис)
  visitTargetId: number;      // Client.id или 999000000 + Office.code
  displayName: string;        // "Клиент" или "Клиент (Офис)"
  startedAt: string;
  note: string | null;
  durationMinutes: number;
}

export interface Visit {
  id: string;
  clientId: number;
  officeId: string | null;  // null = главный офис
  employeeId: string;
  weekCode: string;
  priority: VisitPriority;
  status: VisitStatus;
  plannedDate: string | null;
  plannedTime: string | null;
  plannedDurationMinutes: number | null;
  deadline: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationMinutes: number | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  client: ClientBrief;
  canEditTime?: boolean;
  employee?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  office?: {
    id: string;
    name: string;
  } | null;
}

export interface VisitHistoryItem {
  id: string;
  weekCode: string;
  startedAt: string | null;
  endedAt: string | null;
  durationMinutes: number | null;
  note: string | null;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
  };
}
